import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusPill } from "@/components/common";
import { Trophy, Clock, MapPin, Boxes } from "lucide-react";
import RfqMessageThread from "@/components/RfqMessageThread";
import AnonymityNotice from "@/components/AnonymityNotice";
import FormSection from "@/components/FormSection";
import { AutosaveIndicator, RestoreDraftBanner } from "@/components/AutosaveIndicator";
import useAutosave from "@/hooks/useAutosave";

export default function RfqDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [rfq, setRfq] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [err, setErr] = useState("");
  const [showQuoteForm, setShowQuoteForm] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/rfqs/${id}`);
      setRfq(r.data);
      const q = await api.get(`/quotes?rfq_id=${id}`);
      setQuotes(q.data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, [id]);

  const award = async (qid) => {
    if (!window.confirm("Award this quote? Supplier identity will be revealed and an order will be created.")) return;
    try {
      await api.post(`/rfqs/${id}/award`, { quote_id: qid });
      nav("/app/orders");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const duplicate = async () => {
    try { const { data } = await api.post(`/rfqs/${id}/duplicate`); nav(`/app/rfqs/${data.id}`); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const submitDraft = async () => {
    try { await api.post(`/rfqs/${id}/submit`); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  if (!rfq) return <div style={{ color: "var(--ink-mute)" }}>{err || "Loading RFQ…"}</div>;

  const isBuyer = user.role === "buyer";
  const isSupplier = user.role === "supplier";
  const isAdmin = user.role === "admin";

  return (
    <>
      <PageHeader
        title={rfq.product_name}
        subtitle={<span className="font-mono" style={{ color: "var(--amber-400)" }}>{rfq.code} · {rfq.category}</span>}
        actions={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <StatusPill status={rfq.status} />
            {isBuyer && rfq.status === "draft" && <button className="btn btn-primary" data-testid="submit-draft" onClick={submitDraft}>Submit RFQ</button>}
            {isBuyer && rfq.status !== "draft" && <button className="btn btn-ghost" data-testid="duplicate-rfq" onClick={duplicate}>Duplicate</button>}
          </div>
        }
      />

      <div className="card" style={{ padding: 24, marginBottom: 24, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 20 }}>
        <Info icon={Boxes} label="Quantity" value={`${rfq.quantity} ${rfq.unit}`} />
        <Info icon={MapPin} label="Delivery" value={rfq.delivery_region} />
        <Info icon={Clock} label="Expected" value={`${rfq.expected_delivery_days} days`} />
        <Info label="Urgency" value={rfq.urgency.toUpperCase()} />
        <Info label="Spec" value={rfq.spec || "—"} />
        <Info label="Notes" value={rfq.notes || "—"} />
      </div>

      {isSupplier && rfq.status !== "awarded" && rfq.status !== "expired" && (
        <div style={{ marginBottom: 24 }}>
          {!showQuoteForm ? (
            <button className="btn btn-primary rfq-primary-trigger" data-testid="open-quote-form" onClick={() => setShowQuoteForm(true)}>Submit a quote</button>
          ) : <>
            <AnonymityNotice context="quote" />
            <QuoteForm rfq={rfq} onDone={() => { setShowQuoteForm(false); load(); }} onCancel={() => setShowQuoteForm(false)} />
          </>}
        </div>
      )}

      {isBuyer && (
        <div className="card">
          <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div>
              <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--ink-mute)" }}>RANKED QUOTES · {quotes.length}</div>
              <div style={{ fontSize: 12, color: "var(--ink-dim)", marginTop: 4 }}>Weighted: 50% price · 30% lead time · 20% supplier rating. Supplier identity revealed only on award.</div>
            </div>
            {quotes.length > 1 && (
              <button data-testid="open-compare" onClick={() => nav(`/app/rfqs/${id}/compare`)} className="btn btn-primary rfq-primary-trigger" style={{ padding: "8px 14px", fontSize: 13 }}>
                Compare side-by-side →
              </button>
            )}
          </div>
          {quotes.length === 0 ? <div style={{ padding: 32, textAlign: "center", color: "var(--ink-mute)" }}>No quotes yet — suppliers are being notified.</div> : (
            <table className="data">
              <thead><tr><th>Rank</th><th>Supplier</th><th>Unit Price</th><th>Total</th><th>Lead Time</th><th>Validity</th><th>Rating</th><th>Score</th><th></th></tr></thead>
              <tbody>
                {quotes.map((q, i) => (
                  <tr key={q.id} style={{ background: i === 0 && rfq.status !== "awarded" ? "rgba(245,158,11,.05)" : undefined }}>
                    <td data-label="Rank">{i === 0 && rfq.status !== "awarded" ? <span className="pill pill-quoted"><Trophy size={11} /> #1</span> : <span className="font-mono" style={{ color: "var(--ink-mute)" }}>#{i + 1}</span>}</td>
                    <td data-label="Supplier" className="font-mono" style={{ color: "var(--ink)" }}>{q.supplier_code}</td>
                    <td data-label="Unit Price">${q.unit_price.toFixed(2)}</td>
                    <td data-label="Total" style={{ fontWeight: 600 }}>${q.total_price.toFixed(2)}</td>
                    <td data-label="Lead Time">{q.lead_time_days}d</td>
                    <td data-label="Validity">{q.validity_days}d</td>
                    <td data-label="Rating">{q.supplier_rating?.toFixed(1)} ★</td>
                    <td data-label="Score" className="font-mono" style={{ color: "var(--amber-400)" }}>{q.score?.toFixed(1)}</td>
                    <td data-label="">
                      {rfq.status === "awarded" ? (q.status === "accepted" ? <StatusPill status="accepted" /> : <StatusPill status="rejected" />) :
                        isAdmin ? <span style={{ color: "var(--ink-mute)", fontSize: 12 }}>—</span> :
                        <button data-testid={`award-${q.id}`} onClick={() => award(q.id)} className="btn btn-primary" style={{ padding: "6px 14px", fontSize: 12 }}>Award</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {isSupplier && quotes.length > 0 && (
        <div className="card" style={{ marginTop: 24 }}>
          <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--line)" }} className="font-mono">YOUR QUOTE</div>
          <table className="data">
            <thead><tr><th>Unit Price</th><th>Qty</th><th>Total</th><th>Lead</th><th>Validity</th><th>Status</th></tr></thead>
            <tbody>
              {quotes.map(q => (
                <tr key={q.id}>
                  <td data-label="Unit Price">${q.unit_price.toFixed(2)}</td>
                  <td data-label="Qty">{q.quantity}</td>
                  <td data-label="Total">${q.total_price.toFixed(2)}</td>
                  <td data-label="Lead">{q.lead_time_days}d</td>
                  <td data-label="Validity">{q.validity_days}d</td>
                  <td data-label="Status"><StatusPill status={q.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {err && <div style={{ color: "var(--red)", marginTop: 16 }}>{err}</div>}
      <RfqMessageThread rfqId={id} userRole={user.role} />
    </>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--ink-mute)", textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 600, marginBottom: 8 }}>
        {Icon && <Icon size={12} />} {label}
      </div>
      <div style={{ fontSize: 15, color: "var(--ink)" }}>{value}</div>
    </div>
  );
}

function QuoteForm({ rfq, onDone, onCancel }) {
  const initial = useMemo(() => ({
    unit_price: "", quantity: rfq.quantity, pack_size: 1,
    lead_time_days: 7, validity_days: 14, origin: "Canada",
    shipping_terms: "FOB Origin", remarks: "", alternate_brand: ""
  }), [rfq.quantity]);

  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [touched, setTouched] = useState({});
  const markTouched = (k) => setTouched(t => ({ ...t, [k]: true }));
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const errors = useMemo(() => {
    const e = {};
    const price = parseFloat(form.unit_price);
    if (!form.unit_price || isNaN(price) || price <= 0) e.unit_price = "Enter a unit price greater than 0.";
    const qty = parseInt(form.quantity);
    if (!qty || qty < 1) e.quantity = "Quantity must be at least 1.";
    const pack = parseInt(form.pack_size);
    if (!pack || pack < 1) e.pack_size = "Pack size must be at least 1.";
    const lead = parseInt(form.lead_time_days);
    if (!lead || lead < 1) e.lead_time_days = "Lead time must be at least 1 day.";
    const valid = parseInt(form.validity_days);
    if (!valid || valid < 1) e.validity_days = "Quote validity must be at least 1 day.";
    return e;
  }, [form]);
  const hasErrors = Object.keys(errors).length > 0;
  const showError = (k) => touched[k] && errors[k];

  const totalCAD = useMemo(() => {
    const p = parseFloat(form.unit_price);
    const q = parseInt(form.quantity);
    if (!p || !q || isNaN(p) || isNaN(q)) return null;
    return p * q;
  }, [form.unit_price, form.quantity]);

  const { restored, status, lastSavedAt, clear, dismissRestore } = useAutosave({
    storageKey: `quote-draft-${rfq.id}-v1`,
    data: form,
    isEmpty: (d) => !d.unit_price && !d.remarks?.trim() && !d.alternate_brand?.trim(),
  });

  const resumeDraft = () => {
    if (restored?.data) setForm(f => ({ ...f, ...restored.data }));
    dismissRestore();
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (hasErrors) {
      setTouched({ unit_price: true, quantity: true, pack_size: true, lead_time_days: true, validity_days: true });
      setErr("Please fix the highlighted fields before submitting.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/quotes", {
        rfq_id: rfq.id,
        unit_price: parseFloat(form.unit_price),
        quantity: parseInt(form.quantity),
        pack_size: parseInt(form.pack_size),
        lead_time_days: parseInt(form.lead_time_days),
        validity_days: parseInt(form.validity_days),
        origin: form.origin, shipping_terms: form.shipping_terms,
        remarks: form.remarks, alternate_brand: form.alternate_brand || null,
      });
      clear();
      onDone();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const handleCancel = () => {
    if (busy) return;
    onCancel();
  };

  return (
    <form onSubmit={submit} className="card" style={{ padding: "clamp(18px, 4vw, 24px)", display: "grid", gap: 22 }} data-testid="quote-form">
      <div className="form-card-header">
        <div className="font-serif-display" style={{ fontSize: 20, fontWeight: 600 }}>Submit your quote</div>
        <AutosaveIndicator status={status} lastSavedAt={lastSavedAt} />
      </div>

      {restored && (
        <RestoreDraftBanner savedAt={restored.savedAt} onResume={resumeDraft} onDiscard={dismissRestore} />
      )}

      <FormSection index={1} title="Pricing" hint="Unit price in CAD. We compute totals automatically — anonymity preserved until award." testId="qf-sec-pricing">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          <div className="field">
            <label className="label">Unit price (CAD) *</label>
            <input
              className={`input ${showError("unit_price") ? "input-error" : ""}`}
              type="number" step="0.01" min="0.01"
              data-testid="quote-unit-price"
              value={form.unit_price}
              onChange={e => set("unit_price", e.target.value)}
              onBlur={() => markTouched("unit_price")}
              required
            />
            {showError("unit_price") && <div className="field-error">{errors.unit_price}</div>}
          </div>
          <div className="field">
            <label className="label">Quantity</label>
            <input
              className={`input ${showError("quantity") ? "input-error" : ""}`}
              type="number" min="1" value={form.quantity}
              onChange={e => set("quantity", e.target.value)}
              onBlur={() => markTouched("quantity")}
            />
            {showError("quantity") && <div className="field-error">{errors.quantity}</div>}
          </div>
          <div className="field">
            <label className="label">Pack size</label>
            <input
              className={`input ${showError("pack_size") ? "input-error" : ""}`}
              type="number" min="1" value={form.pack_size}
              onChange={e => set("pack_size", e.target.value)}
              onBlur={() => markTouched("pack_size")}
            />
            {showError("pack_size") && <div className="field-error">{errors.pack_size}</div>}
          </div>
        </div>
        {totalCAD !== null && (
          <div className="field-hint" data-testid="quote-total-preview">
            Estimated total: <strong style={{ color: "var(--ink)" }}>${totalCAD.toFixed(2)} CAD</strong> (unit × quantity)
          </div>
        )}
      </FormSection>

      <FormSection index={2} title="Lead time & validity" hint="When can you ship, and how long is this quote good for?" testId="qf-sec-lead">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
          <div className="field">
            <label className="label">Lead time (days) *</label>
            <input
              className={`input ${showError("lead_time_days") ? "input-error" : ""}`}
              type="number" min="1"
              data-testid="quote-lead-time"
              value={form.lead_time_days}
              onChange={e => set("lead_time_days", e.target.value)}
              onBlur={() => markTouched("lead_time_days")}
            />
            {showError("lead_time_days") && <div className="field-error">{errors.lead_time_days}</div>}
          </div>
          <div className="field">
            <label className="label">Quote validity (days) *</label>
            <input
              className={`input ${showError("validity_days") ? "input-error" : ""}`}
              type="number" min="1" value={form.validity_days}
              onChange={e => set("validity_days", e.target.value)}
              onBlur={() => markTouched("validity_days")}
            />
            {showError("validity_days") && <div className="field-error">{errors.validity_days}</div>}
          </div>
        </div>
      </FormSection>

      <FormSection index={3} title="Logistics & notes" hint="Origin, shipping terms, and any alternates or remarks for the buyer." testId="qf-sec-logistics">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <div className="field">
            <label className="label">Origin</label>
            <input className="input" value={form.origin} onChange={e => set("origin", e.target.value)} placeholder="e.g. Canada" />
          </div>
          <div className="field">
            <label className="label">Shipping terms</label>
            <input className="input" value={form.shipping_terms} onChange={e => set("shipping_terms", e.target.value)} placeholder="e.g. FOB Origin" />
          </div>
        </div>
        <div className="field">
          <label className="label">Alternate brand (if buyer allows)</label>
          <input className="input" value={form.alternate_brand} onChange={e => set("alternate_brand", e.target.value)} placeholder="Optional — leave blank if matching exact brand" />
        </div>
        <div className="field">
          <label className="label">Remarks</label>
          <textarea
            className="input"
            style={{ minHeight: 90, resize: "vertical" }}
            value={form.remarks}
            onChange={e => set("remarks", e.target.value)}
            placeholder="Anything the buyer should know — substitutions, lead-time caveats, packing."
          />
          <div className="field-hint">Anonymity is enforced — emails, phone numbers, URLs, and company names are auto-blocked.</div>
        </div>
      </FormSection>

      {err && <div style={{ color: "var(--red)", fontSize: 13 }} data-testid="quote-form-error">{err}</div>}
      <div className="form-action-row">
        <button className="btn btn-primary" data-testid="submit-quote" disabled={busy || hasErrors} title={hasErrors ? "Fix highlighted fields" : ""}>
          {busy ? "Sending…" : "Submit quote"}
        </button>
        <button type="button" className="btn btn-ghost" onClick={handleCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}
